import axios from 'axios';
import { BASE_URLS, STORAGE_KEYS } from '../utils/constants';

const API_BASE_URL = BASE_URLS[process.env.REACT_APP_APP_ENV || 'local'];

const getAuthHeaders = () => {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

export const generateSignedUrls = async (files, entityType, entityId, options = {}) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/files/generate-signed-url`,
      {
        files: files.map((file) => ({
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
        })),
        entityType,
        entityId,
        isPublic: options.isPublic || false,
        expiresIn: options.expiresIn,
      },
      {
        headers: getAuthHeaders(),
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error generating signed URLs:', error);
    throw error;
  }
};

export const uploadFileToCloud = async (file, signedUrl, onProgress) => {
  try {
    const headers = {
      'Content-Type': file.type,
    };

    if (signedUrl.includes('blob.core.windows.net')) {
      headers['x-ms-blob-type'] = 'BlockBlob';
    }

    await axios.put(signedUrl, file, {
      headers,
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });

    return { success: true };
  } catch (error) {
    console.error('Error uploading file to cloud:', error);
    throw error;
  }
};

export const confirmUpload = async (fileId, status, errorMessage = null) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/files/confirm-upload`,
      {
        fileId,
        status,
        errorMessage,
      },
      {
        headers: getAuthHeaders(),
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error confirming upload:', error);
    throw error;
  }
};

export const confirmBatchUpload = async (batchId, results) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/files/batch/confirm`,
      {
        batchId,
        results,
      },
      {
        headers: getAuthHeaders(),
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error confirming batch upload:', error);
    throw error;
  }
};

export const generateDownloadUrl = async (fileId, expiresIn = 3600) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/files/generate-download-url`,
      {
        fileId,
        expiresIn,
      },
      {
        headers: getAuthHeaders(),
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error generating download URL:', error);
    throw error;
  }
};

export const generateDownloadUrlByKey = async (fileKey, expiresIn = 3600) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/files/generate-download-url`,
      {
        fileKey,
        expiresIn,
      },
      {
        headers: getAuthHeaders(),
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error generating download URL by key:', error);
    throw error;
  }
};

export const deleteFiles = async (fileIds) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/files/delete`, {
      headers: getAuthHeaders(),
      data: { fileIds },
    });

    return response.data;
  } catch (error) {
    console.error('Error deleting files:', error);
    throw error;
  }
};

export const listFiles = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const response = await axios.get(`${API_BASE_URL}/files/list?${queryParams}`, {
      headers: getAuthHeaders(),
    });

    return response.data;
  } catch (error) {
    console.error('Error listing files:', error);
    throw error;
  }
};

export const getFileById = async (fileId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/files/${fileId}`, {
      headers: getAuthHeaders(),
    });

    return response.data;
  } catch (error) {
    console.error('Error getting file:', error);
    throw error;
  }
};

export const getBatchStatus = async (batchId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/files/batch/${batchId}`, {
      headers: getAuthHeaders(),
    });

    return response.data;
  } catch (error) {
    console.error('Error getting batch status:', error);
    throw error;
  }
};

export const uploadSingleFile = async (
  file,
  entityType,
  entityId,
  options = {},
  onProgress
) => {
  try {
    const signedUrlResponse = await generateSignedUrls(
      [file],
      entityType,
      entityId,
      options
    );

    const uploadUrl = signedUrlResponse.data.uploadUrls[0];

    await uploadFileToCloud(file, uploadUrl.uploadUrl, onProgress);

    await confirmUpload(uploadUrl.fileId, 'completed');

    return {
      success: true,
      fileId: uploadUrl.fileId,
      fileKey: uploadUrl.fileKey,
    };
  } catch (error) {
    console.error('Error in single file upload:', error);
    throw error;
  }
};

export const uploadBulkFiles = async (
  files,
  entityType,
  entityId,
  options = {},
  onProgressUpdate
) => {
  try {
    const signedUrlResponse = await generateSignedUrls(
      files,
      entityType,
      entityId,
      options
    );

    const { batchId, uploadUrls } = signedUrlResponse.data;
    const CONCURRENT_UPLOADS = options.concurrentUploads || 10;

    const uploadQueue = [...uploadUrls];
    const results = [];
    let activeUploads = 0;
    let completedCount = 0;
    let failedCount = 0;

    return new Promise((resolve, reject) => {
      const processNext = () => {
        if (uploadQueue.length === 0 && activeUploads === 0) {
          confirmBatchUpload(batchId, results)
            .then(() => {
              resolve({
                success: true,
                batchId,
                totalFiles: files.length,
                successful: completedCount,
                failed: failedCount,
                results,
              });
            })
            .catch(reject);
          return;
        }

        while (activeUploads < CONCURRENT_UPLOADS && uploadQueue.length > 0) {
          const uploadInfo = uploadQueue.shift();
          const fileIndex = uploadUrls.indexOf(uploadInfo);
          const file = files[fileIndex];

          activeUploads++;

          uploadFileToCloud(file, uploadInfo.uploadUrl)
            .then(() => {
              results.push({ fileId: uploadInfo.fileId, status: 'completed' });
              completedCount++;
            })
            .catch((error) => {
              results.push({
                fileId: uploadInfo.fileId,
                status: 'failed',
                error: error.message,
              });
              failedCount++;
            })
            .finally(() => {
              activeUploads--;

              if (onProgressUpdate) {
                onProgressUpdate({
                  total: files.length,
                  completed: completedCount,
                  failed: failedCount,
                  progress: Math.round(
                    ((completedCount + failedCount) / files.length) * 100
                  ),
                });
              }

              processNext();
            });
        }
      };

      processNext();
    });
  } catch (error) {
    console.error('Error in bulk file upload:', error);
    throw error;
  }
};
